import type { KeyboardEvent } from 'react';

/**
 * Guards for numeric program fields — reps, weights, durations, RIR.
 *
 * None of these quantities can be negative, but `<input type="number">` happily accepts a minus
 * sign even with `min="0"`: the attribute only feeds native form validation, which these inline,
 * save-as-you-type inputs never run. So a coach could type `-2` into RIR and have it saved.
 *
 * Two layers, because there are three ways a negative gets in:
 *   - typing `-`      → blocked by {@link blockNegativeKeys}
 *   - pasting `-5`    → caught by {@link nonNegative}
 *   - spinner arrows  → stopped by `min: 0` (browsers do clamp the stepper), with
 *                       {@link nonNegative} as the backstop
 *
 * `e`/`E` are blocked alongside `-` because exponent notation is another way to reach a negative
 * (`1e-5`), and no field here has a use for it.
 */

/** `onKeyDown` for a non-negative number input: swallows `-`, `+` and exponent keys. */
export const blockNegativeKeys = (e: KeyboardEvent): void => {
    if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
        e.preventDefault();
    }
};

/**
 * Normalises a raw `<input type="number">` value, clearing anything negative.
 *
 * Returns the value UNCHANGED when it is empty or non-negative — empty and `0` are both legitimate
 * for these fields — so it can wrap an existing `e.target.value` without altering what the field
 * already accepted. A negative comes back as `''` (rather than `0`) so the field simply stays empty
 * instead of silently inventing a value.
 *
 * The leading-minus check is what `Number(raw) < 0` misses: `-0` is not less than zero, and
 * `-1e-400` underflows to `-0` as well.
 */
export const nonNegative = (raw: string): string =>
    raw !== '' && (raw.trimStart().startsWith('-') || Number(raw) < 0) ? '' : raw;

/** Props to spread onto a non-negative number input (or into MUI's `inputProps`). */
export const nonNegativeInputProps = {
    min: 0,
    onKeyDown: blockNegativeKeys,
};
